import React, { useState, useRef, useEffect } from 'react';
import SignatureField, { SIGNATURE_BUTTON_PLAIN } from './UI/SignatureField';
import TermsAndConditions from '../components/TermsAndConditions';
import ClientInformationConsentAgreement from './ClientInformationConsentAgreement';
import DebitOrderConsentAgreement from './DebitOrderConsentAgreement';
import AnnexureA from './AnnexureA';
import CameraCapture from './CameraCapture';
import { apiUrl } from '../config/api';
import PhoneInput from './UI/PhoneInput';
import TownInput from './UI/TownInput';
import { matchNamibianTown } from '../utils/namibianTowns';
import { NAMIBIAN_BANKS, ACCOUNT_TYPES } from './Forms/BankInfoForm';
import { getCompanyConfigCached } from '../utils/invoiceUtils';
import { openDocument } from '../utils/documentLinks';
import { formatDateDMY } from '../utils/dateUtils';
import { toCurrency } from '../utils/adminMetrics';
import DocumentTile, { DOCUMENT_GRID } from './UI/DocumentTile';

// Shared section wrapper used for all contract-related sections (matches signed contract styling)
const ContractSectionCard = ({ title, children, className = '' }) => (
  <div className={`mb-8 border-2 border-gray-900 dark:border-slate-600 overflow-hidden rounded-none bg-white dark:bg-slate-800 ${className}`}>
    <div className="bg-slate-600 text-white px-3 py-2 font-semibold text-xs uppercase tracking-wide">
      {title}
    </div>
    {children}
  </div>
);

// A part of the signing section (References, payout, Signatures): its own
// heading bar over a light panel, instead of one frame around everything.
const SigningPart = ({ title, required = false, children, className = '', id }) => (
  <section id={id} className={`mb-6 scroll-mt-4 rounded-md transition-shadow ${className}`}>
    <div className="bg-slate-600 text-white px-4 py-2 text-xs font-semibold uppercase tracking-wide rounded-t-md">
      {title}{required && <span className="text-red-300"> *</span>}
    </div>
    <div className="p-4 bg-white dark:bg-slate-800 border border-t-0 border-gray-200 dark:border-slate-600 rounded-b-md">
      {children}
    </div>
  </section>
);


const ContractPreview = ({ loanData, clientData, onEdit, onGenerate, loading, references, setReferences, initialValues, requiredPhotoFile: propRequiredPhotoFile, requiredPhotoPreview: propRequiredPhotoPreview, setRequiredPhotoFile: propSetRequiredPhotoFile, setRequiredPhotoPreview: propSetRequiredPhotoPreview, readOnly = false, showPreviewHeader = true, hideDocuments = false }) => {
  // Use local state if references prop is not provided (for staff/admin)
  const [localReferences, setLocalReferences] = useState({
    reference1_name: clientData?.reference1_name || '',
    reference1_tel: clientData?.reference1_tel || '',
    reference2_name: clientData?.reference2_name || '',
    reference2_tel: clientData?.reference2_tel || ''
  });

  // How the client wants the money once the loan is approved (mandatory,
  // asked with the references): eWallet to a mobile number, or EFT to a
  // bank account. A loan that has it already opens with it filled in.
  const [payout, setPayout] = useState(() => ({
    method: loanData?.payout_method || '',
    ewallet_number: loanData?.payout_ewallet_number || '',
    bank_name: loanData?.payout_bank_name || '',
    branch_code: loanData?.payout_branch_code || '',
    account_number: loanData?.payout_account_number || '',
    account_type: loanData?.payout_account_type || '',
    account_holder: loanData?.payout_account_holder || '',
  }));
  // What is still missing, in plain words (empty when complete).
  const payoutMissing = (() => {
    if (payout.method !== 'eWallet' && payout.method !== 'EFT') return ['how you want to receive the money'];
    if (payout.method === 'eWallet') {
      return String(payout.ewallet_number || '').replace(/\D/g, '').length >= 8 ? [] : ['the eWallet mobile number'];
    }
    return [
      !String(payout.bank_name || '').trim() && 'bank',
      !/^\d{5,20}$/.test(String(payout.account_number || '').replace(/\s/g, '')) && 'account number (digits)',
      !String(payout.account_type || '').trim() && 'account type',
      !String(payout.account_holder || '').trim() && 'account holder',
    ].filter(Boolean);
  })();

  const [resolvedClientData, setResolvedClientData] = useState(clientData || null);
  const [clientProfileLoading, setClientProfileLoading] = useState(false);
  
  // State for documents fetched from the API
  const [fetchedDocuments, setFetchedDocuments] = useState([]);
  const [documentsLoading, setDocumentsLoading] = useState(false);
  // The loan's documents, notes and stored signatures have been looked up.
  const [signaturesChecked, setSignaturesChecked] = useState(false);
  const [clientIdentificationDocs, setClientIdentificationDocs] = useState([]);
  const [clientNotes, setClientNotes] = useState([]);
  const [signaturesData, setSignaturesData] = useState(null);
  const [uploadingDoc, setUploadingDoc] = useState(false);
  const [companyConfig, setCompanyConfig] = useState(null);
  // Required photo state for Pending contract preview (local fallback if parent doesn't supply props)
  const [localRequiredPhotoFile, setLocalRequiredPhotoFile] = useState(null);
  const [localRequiredPhotoPreview, setLocalRequiredPhotoPreview] = useState(null);

  // Effective values: prefer props passed from parent (ClientDashboard) so captured image there is used
  const effectiveRequiredPhotoFile = propRequiredPhotoFile !== undefined && propRequiredPhotoFile !== null ? propRequiredPhotoFile : localRequiredPhotoFile;
  const effectiveRequiredPhotoPreview = propRequiredPhotoPreview !== undefined && propRequiredPhotoPreview !== null ? propRequiredPhotoPreview : localRequiredPhotoPreview;
  
  const effectiveReferences = references ?? localReferences;
  const effectiveSetReferences = setReferences ?? setLocalReferences;
  
  // Determine if this is client-side review (references are controlled by parent)
  // This should be strictly based on whether the component is being used by a client or staff/admin
  const isClientReview = Boolean(references && setReferences);
  const isReadOnly = Boolean(readOnly);

  useEffect(() => {
    setResolvedClientData(prev => {
      // Only reset if the client actually changed (different id), not just a new object reference
      const prevId = prev?.id;
      const newId = clientData?.id;
      if (prevId && newId && prevId !== newId) return clientData || null;
      if (!prev) return clientData || null;
      return prev;
    });
  }, [clientData?.id]);

  const effectiveClientData = resolvedClientData || clientData;
  const effectiveClientId =
    effectiveClientData?.id ?? effectiveClientData?.id ?? loanData?.client_id ?? loanData?.clientId ?? null;

  const flattenClientProfile = (profile) => {
    const pi = profile?.personal_info || {};
    const oi = profile?.occupation_info || {};
    const bi = profile?.bank_info || {};
    return {
      id: pi.id,
      first_name: pi.first_name,
      middle_name: pi.middle_name,
      last_name: pi.last_name,
      id_passport_no: pi.id_passport_no,
      email: pi.email,
      phone_number: pi.phone_number,
      postal_address: pi.postal_address,
      erf_no: pi.erf_no,
      street_name: pi.street_name,
      suburb: pi.suburb,
      city: pi.city,
      country: pi.country,
      residential_address: pi.residential_address,
      marital_status: pi.marital_status,
      occupation: oi.occupation,
      employer_name: oi.employer_name,
      payslip_number: oi.payslip_number,
      employer_address: oi.employer_address,
      employer_phone: oi.employer_phone,
      bank_name: bi.bank_name,
      account_number: bi.account_number,
      branch_code: bi.branch_code,
      account_type: bi.account_type
    };
  };

  useEffect(() => {
    const hasName = Boolean(effectiveClientData?.first_name && String(effectiveClientData.first_name).trim());
    const hasEmploymentData = Boolean(effectiveClientData?.occupation || effectiveClientData?.employer_name);
    if ((hasName && hasEmploymentData) || !effectiveClientId) return;

    let cancelled = false;
    const load = async () => {
      const token = localStorage.getItem('token');
      if (!token) return;
      setClientProfileLoading(true);
      try {
        // Staff/admin flow: load by client id
        const staffRes = await fetch(apiUrl(`/api/clients/${encodeURIComponent(String(effectiveClientId))}/profile`), {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (staffRes.ok) {
          const prof = await staffRes.json();
          const flat = flattenClientProfile(prof);
          if (!cancelled) {
            setResolvedClientData(prev => {
              const base = { ...(prev || {}), id: flat.id || effectiveClientId };
              Object.keys(flat).forEach(k => { if (flat[k] !== null && flat[k] !== undefined) base[k] = flat[k]; });
              return base;
            });
          }
          return;
        }

        // Client flow fallback
        const myRes = await fetch(apiUrl('/api/clients/my-profile'), {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (myRes.ok) {
          const prof = await myRes.json();
          const flat = flattenClientProfile(prof);
          if (!cancelled) {
            setResolvedClientData(prev => {
              const base = { ...(prev || {}), id: flat.id || effectiveClientId };
              Object.keys(flat).forEach(k => { if (flat[k] !== null && flat[k] !== undefined) base[k] = flat[k]; });
              return base;
            });
          }
        }
      } catch (e) {
        // best-effort only
      } finally {
        if (!cancelled) setClientProfileLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [effectiveClientId, effectiveClientData?.first_name]);
  
  // Log client data for debugging
  useEffect(() => {
    console.log('ContractPreview received clientData:', {
      name: `${effectiveClientData?.first_name || 'N/A'} ${effectiveClientData?.last_name || 'N/A'}`,
      address: effectiveClientData?.residential_address || 'N/A', 
      bank: effectiveClientData?.bank_name || 'N/A'},
      isClientReview
    );
  }, [effectiveClientData, isClientReview]);
  
  // Effect to fetch documents for both client and staff modes
  useEffect(() => {
    // load company configuration for dynamic lender details
    (async () => {
      try { const cfg = await getCompanyConfigCached(); setCompanyConfig(cfg); } catch {}
    })();
    if (loanData?.id) {
      setDocumentsLoading(true);
      
          const fetchDocuments = async () => {
        try {
          const token = localStorage.getItem('token');
          if (!token) {
            console.warn('No auth token found; cannot fetch documents/notes/signatures');
            setFetchedDocuments([]);
            setClientNotes([]);
            setSignaturesData(null);
            setDocumentsLoading(false);
            return;
          }
          // Use the same document endpoint used elsewhere in the app (singular "document")
          const response = await fetch(apiUrl(`/api/document/loan/${loanData.id}`), {
            headers: {
              'Authorization': `Bearer ${token}`
            }
          });
          if (!response.ok) throw new Error('Failed to fetch documents');
          const data = await response.json();
          const sanitizedLoanDocs = (data.documents || []).map(doc => ({ ...doc, document_scope: 'loan' }));
          setFetchedDocuments(sanitizedLoanDocs);

              // Fetch client-message notes
              try {
                const notesRes = await fetch(apiUrl(`/api/loans/${loanData.id}/notes`), {
                  headers: { 'Authorization': `Bearer ${token}` }
                });
                if (notesRes.ok) {
                  const notesJson = await notesRes.json();
                  // notes endpoint may return an array or { notes: [] }
                  const notesArray = Array.isArray(notesJson) ? notesJson : (Array.isArray(notesJson.notes) ? notesJson.notes : []);
                  setClientNotes(notesArray || []);
                }
              } catch (noteErr) {
                console.warn('Failed to fetch loan notes for client:', noteErr);
              }

              // Fetch signatures to pre-fill references if present
              try {
                const sigRes = await fetch(apiUrl(`/api/loans/${loanData.id}/signatures`), {
                  headers: { 'Authorization': `Bearer ${token}` }
                });
                if (sigRes.ok) {
                  const sigJson = await sigRes.json();
                  if (sigJson) {
                    setSignaturesData(sigJson);
                    // Parse references_json safely
                    try {
                      const refs = sigJson.references_json ? (typeof sigJson.references_json === 'string' ? JSON.parse(sigJson.references_json) : sigJson.references_json) : null;
                      if (refs) {
                        // If parent provided controlled references prop exists, populate it only if fields are empty
                        const parentHasRefs = Boolean(references);
                        if (parentHasRefs && typeof effectiveSetReferences === 'function') {
                          const allEmpty = !Object.values(references).some(v => v && String(v).trim() !== '');
                          if (allEmpty) {
                            effectiveSetReferences(prev => ({ ...prev, reference1_name: refs.reference1_name || prev.reference1_name, reference1_tel: refs.reference1_tel || prev.reference1_tel, reference2_name: refs.reference2_name || prev.reference2_name, reference2_tel: refs.reference2_tel || prev.reference2_tel }));
                          }
                        } else if (!references) {
                          setLocalReferences(prev => ({ ...prev, reference1_name: refs.reference1_name || prev.reference1_name, reference1_tel: refs.reference1_tel || prev.reference1_tel, reference2_name: refs.reference2_name || prev.reference2_name, reference2_tel: refs.reference2_tel || prev.reference2_tel }));
                        }
                      }
                    } catch (parseErr) {
                      console.warn('Failed to parse signatures.references_json', parseErr);
                    }
                  }
                }
              } catch (sigErr) {
                console.warn('Failed to fetch loan signatures for prefill:', sigErr);
              }
        } catch (error) {
          console.error('Error fetching documents:', error);
          setFetchedDocuments([]);
        } finally {
          setDocumentsLoading(false);
          setSignaturesChecked(true);
        }
      };
      
      fetchDocuments();
    }
  }, [loanData?.id]);

  useEffect(() => {
    const fetchClientIdentification = async () => {
      if (isClientReview || !effectiveClientId) {
        setClientIdentificationDocs([]);
        return;
      }

      const isIdentificationType = (documentType) => {
        const t = String(documentType || '').toLowerCase().trim();
        if (!t) return false;
        // Avoid false positives like "residence" containing "id"
        if (t.includes('proof of residence') || t.includes('proof of address') || t.includes('residence')) return false;
        if (t.includes('identification') || t.includes('passport') || t.includes('client id') || t.includes('id card')) return true;
        // Match standalone "id" (word boundary), including common variants
        if (/\bid\b/.test(t)) return true;
        if (t === 'id document' || t === 'id-doc' || t === 'id_doc') return true;
        return false;
      };

      try {
        const token = localStorage.getItem('token');
        if (!token) return;
        const res = await fetch(apiUrl(`/api/document/client/${effectiveClientId}`), {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (!res.ok) throw new Error('Failed to fetch client documents');
        const docs = await res.json();
        const idDocs = (docs || [])
          .filter(doc => isIdentificationType(doc?.document_type))
          .map(doc => {
            const { file_data, ...rest } = doc;
            return { ...rest, document_scope: 'client-id' };
          });
        setClientIdentificationDocs(idDocs);
      } catch (err) {
        console.warn('Failed to fetch client identification docs:', err?.message || err);
        setClientIdentificationDocs([]);
      }
    };

    fetchClientIdentification();
  }, [effectiveClientId, isClientReview, loanData?.id, uploadingDoc]);

  // Upload handler for client to re-upload documents during Contract Review
  
  // Add state for signatures
  const [signature, setSignature] = useState(initialValues?.signature || null);
  // Lender signature (shown for Walk-in preview only)
  const [lenderSignature, setLenderSignature] = useState(initialValues?.lenderSignature || null);
  const [witness1Signature, setWitness1Signature] = useState(initialValues?.witness1Signature || null);
  const [witness2Signature, setWitness2Signature] = useState(null);

  // Add state for initials
  const [borrowerInitials, setBorrowerInitials] = useState(initialValues?.initials?.borrowerInitials || null);
  const [lenderInitials, setLenderInitials] = useState(initialValues?.initials?.lenderInitials || null);
  const [witness1Initials, setWitness1Initials] = useState(initialValues?.initials?.witness1Initials || null);
  const [witness2Initials, setWitness2Initials] = useState(null);
  
  // Show lender capture during staff review for Online loans (Awaiting Staff) and for Walk-in flows
  const isOnlineAwaitingStaff = loanData?.application_type === 'Online' && loanData?.status === 'Awaiting Staff';
  const showLenderBlock = loanData?.application_type === 'Walk-in' || isOnlineAwaitingStaff;

  // Resubmit mode: client reviewing an Online loan with a staff note while in Awaiting Client
  // Whether the client may resubmit is the API's call (loan.state.actions),
  // the same rule the dashboard uses: a loan staff updated and sent back
  // without a note must still be resubmittable. Older loan objects without
  // state fall back to the previous rule.
  const hasNote = Array.isArray(clientNotes) && clientNotes.length > 0;
  const apiSaysResubmit = loanData?.state?.actions?.resubmit;
  const canResubmit = Boolean(isClientReview && (
    apiSaysResubmit !== undefined
      ? apiSaysResubmit === true
      : loanData?.application_type === 'Online' && loanData?.status === 'Awaiting Client' && hasNote
  ));
  
  // Allow clients to recapture signatures even when the modal loads in read-only view
  const [recaptureMode, setRecaptureMode] = useState(false);
  // Sent back to the client without ever having been signed (an application
  // the assessors reviewed after the calculator declined it): nothing stored
  // to reuse, so the client signs as for a new application.
  const hasStoredSignatures = Boolean(signaturesData?.borrower_signature || initialValues?.signature);
  const needsFirstSignatures = Boolean(
    isClientReview && isReadOnly && loanData?.status === 'Awaiting Client' && signaturesChecked && !hasStoredSignatures
  );
  const signaturesLocked = isReadOnly && !recaptureMode && !needsFirstSignatures;
  const signatureEditingEnabled = !signaturesLocked;
  // Allow references to be visible/editable during Awaiting Client review even when signatures are locked
  const showReferenceSection = Boolean(signatureEditingEnabled || (isClientReview && isReadOnly && loanData?.status === 'Awaiting Client'));

  // Add state for tracking scroll to bottom of terms (clients must always scroll at least once)
  const [termsScrolledToBottom, setTermsScrolledToBottom] = useState(() => (isClientReview ? false : true));
  const termsContainerRef = useRef(null);
  const prevClientReviewRef = useRef(isClientReview);
  const prevCanResubmitRef = useRef(canResubmit);

  useEffect(() => {
    if (prevClientReviewRef.current !== isClientReview) {
      prevClientReviewRef.current = isClientReview;
      setTermsScrolledToBottom(isClientReview ? false : true);
    }
  }, [isClientReview]);

  useEffect(() => {
    if (canResubmit && !prevCanResubmitRef.current) {
      setTermsScrolledToBottom(false);
    }
    prevCanResubmitRef.current = canResubmit;
  }, [canResubmit]);

  // Add state for error message
  const [errorMessage, setErrorMessage] = useState('');
  // Good news (green), apart from the errors (red).
  const [notice, setNotice] = useState('');

  // Add state for signature locations
  const [signatureLocations, setSignatureLocations] = useState(
    initialValues?.signatureLocations || {
      // Typed by the client and chosen from the towns list (mandatory).
      borrowerLocation: '',
      lenderLocation: 'Windhoek'
    }
  );
  
  // Scroll tracking function for the terms container
  const handleTermsScroll = () => {
    if (!isClientReview || !termsContainerRef.current) return;
    const { scrollTop, scrollHeight, clientHeight } = termsContainerRef.current;
    const isAtBottom = scrollHeight - scrollTop - clientHeight < 20;
    if (isAtBottom && !termsScrolledToBottom) {
      setTermsScrolledToBottom(true);
    }
  };
  
  // Add effect to show message when terms are scrolled to bottom
  useEffect(() => {
    if (isClientReview && termsScrolledToBottom) {
      setNotice('Thank you for reading the terms. You can now continue.');
      const timer = setTimeout(() => setNotice(''), 3000);
      return () => clearTimeout(timer);
    }
  }, [termsScrolledToBottom, isClientReview]);

  // Handle input changes
  const handleReferenceChange = (e) => {
    const { name, value } = e.target;
    if (typeof effectiveSetReferences === 'function') {
      effectiveSetReferences(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  // Handle location input changes
  
  const clearClientSignatureState = () => {
    setSignature(null);
    setBorrowerInitials(null);
    setWitness1Signature(null);
    setWitness1Initials(null);
  };

  const restoreStoredSignatures = () => {
    const borrowerSig = signaturesData?.borrower_signature ?? initialValues?.signature ?? null;
    const borrowerInit = signaturesData?.borrower_initials ?? initialValues?.initials?.borrowerInitials ?? null;
    const witnessSig = signaturesData?.witness1_signature ?? initialValues?.witness1Signature ?? null;
    const witnessInit = signaturesData?.witness1_initials ?? initialValues?.initials?.witness1Initials ?? null;

    setSignature(borrowerSig || null);
    setBorrowerInitials(borrowerInit || null);
    setWitness1Signature(witnessSig || null);
    setWitness1Initials(witnessInit || null);
  };

  const fieldSetterMap = {
    borrowerSignature: setSignature,
    borrowerInitials: setBorrowerInitials,
    witness1Signature: setWitness1Signature,
    witness1Initials: setWitness1Initials
  };

  const getStoredFieldValue = (field) => {
    switch (field) {
      case 'borrowerSignature':
        return signaturesData?.borrower_signature ?? initialValues?.signature ?? null;
      case 'borrowerInitials':
        return signaturesData?.borrower_initials ?? initialValues?.initials?.borrowerInitials ?? null;
      case 'witness1Signature':
        return signaturesData?.witness1_signature ?? initialValues?.witness1Signature ?? null;
      case 'witness1Initials':
        return signaturesData?.witness1_initials ?? initialValues?.initials?.witness1Initials ?? null;
      default:
        return null;
    }
  };


  const cancelSingleFieldRecapture = (field) => {
    const setter = fieldSetterMap[field];
    if (!setter) return;
    setter(getStoredFieldValue(field));
  };




  const handleStartRecapture = () => {
    clearClientSignatureState();
    setRecaptureMode(true);
    setTermsScrolledToBottom(false);
    setErrorMessage('');
  };

  const handleCancelRecapture = () => {
    restoreStoredSignatures();
    setRecaptureMode(false);
    setErrorMessage('');
  };

  useEffect(() => {
    if (!recaptureMode && isReadOnly && !needsFirstSignatures) {
      restoreStoredSignatures();
    }
  }, [signaturesData, recaptureMode, isReadOnly, needsFirstSignatures]);
  
  // Check for missing client data and show an error if needed
  useEffect(() => {
    if (!clientData || !effectiveClientData.first_name) {
      console.error('Missing client data in ContractPreview!');
      setErrorMessage('Client information is missing. Please refresh the page and try again.');
    } else {
      // Clear any previous error message about missing client data
      if (errorMessage === 'Client information is missing. Please refresh the page and try again.') {
        setErrorMessage('');
      }
    }
  }, [clientData]);

  // A field that is missing: say so, scroll to it, outline it for a moment
  // and put the cursor in it. Returns false for the validation to return.
  const failAt = (message, id) => {
    setErrorMessage(message);
    const el = typeof document !== 'undefined' ? document.getElementById(id) : null;
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      el.classList.add('ring-2', 'ring-red-400', 'ring-offset-2');
      setTimeout(() => el.classList.remove('ring-2', 'ring-red-400', 'ring-offset-2'), 2500);
      const input = el.matches('input, select, textarea') ? el : el.querySelector('input:not([type=hidden]), select, textarea');
      if (input) input.focus({ preventScroll: true });
    }
    return false;
  };

  // Validation for reference details and signature
  const validateForm = () => {
    // The client signs as Borrower only; the agreement has no Witness 1
    if (signaturesLocked) {
      // In read-only mode, skip all validations and allow viewing only
      setErrorMessage("");
      return true;
    }
    if (isClientReview) {
      // Check if terms have been scrolled through
      if (!termsScrolledToBottom) {
        return failAt("Please scroll through and read the entire loan agreement before signing the contract", 'contract-terms');
      }
      if (!effectiveReferences || !effectiveReferences.reference1_name || !effectiveReferences.reference1_tel || 
          !effectiveReferences.reference2_name || !effectiveReferences.reference2_tel) {
        return failAt("Please fill in all reference details before generating the contract", 'contract-references');
      }
      if (payoutMissing.length) {
        return failAt(`How you want to receive the money: enter ${payoutMissing.join(', ')}.`, 'contract-payout');
      }
      if (!matchNamibianTown(signatureLocations.borrowerLocation)) {
        return failAt('Signed at: choose the town you are signing in from the list.', 'signed-at-town');
      }
      if (!signature) {
        return failAt('Please sign as Borrower before generating', 'borrower-signature');
      }
      if (!borrowerInitials) {
        return failAt("Please provide Borrower's initials before generating", 'borrower-initials');
      }
      // If loan is Pending, require client photo to be captured here (mandatory)
      // Accept either a File (effectiveRequiredPhotoFile) or a preview data URL (effectiveRequiredPhotoPreview)
      if (loanData?.status === 'Pending' && !(effectiveRequiredPhotoFile || effectiveRequiredPhotoPreview)) {
        setErrorMessage('Client photo is required before signing the contract');
        return false;
      }
      // Lender and Witness 2 signature/initials are optional for client-side
      setErrorMessage('');
      return true;
    } else {
  // Staff/admin workflow:
  // Only require the Borrower here. Lender + Witness 2 are captured later in the Awaiting Approval modal.
      if (payoutMissing.length) {
        return failAt(`How the client wants to receive the money: enter ${payoutMissing.join(', ')}.`, 'contract-payout');
      }
      if (!matchNamibianTown(signatureLocations.borrowerLocation)) {
        return failAt('Signed at: choose the town from the list.', 'signed-at-town');
      }

      if (!signature) {
        return failAt('Please sign as Borrower before generating', 'borrower-signature');
      }
      if (!borrowerInitials) {
        return failAt("Please provide Borrower's initials before generating", 'borrower-initials');
      }

      // No validation for lender/witness2 at this stage
      setErrorMessage('');
      return true;
    }
  };

  // Modified onGenerate to include references, signature, and initials
  const handleGenerate = () => {
    // Log client data before validation
    console.log('Client data before validation:', effectiveClientData);
    
    if (!validateForm()) {
      return;
    }
    
    // Check if client data exists
    if (clientProfileLoading) {
      setErrorMessage('Loading client data… please wait a moment and try again.');
      return;
    }
    if (!effectiveClientData || !effectiveClientData.first_name || !String(effectiveClientData.first_name).trim()) {
      setErrorMessage("Client data is missing. Please refresh the page and try again.");
      return;
    }
    
    // Create updated client data with references, signature, and initials
    const updatedClientData = {
      ...effectiveClientData,
      // Use effectiveReferences so staff/walk-in flow includes locally entered refs
      ...effectiveReferences,
      signature,
      // Include lender for Walk-in or Online (Awaiting Staff); witness 2 excluded at this stage
      ...(showLenderBlock ? { lenderSignature } : {}),
      witness1Signature,
      initials: {
        borrowerInitials,
        witness1Initials,
        ...(showLenderBlock ? { lenderInitials } : {})
      },
      signatureLocations,
      // How the money is paid out once approved (sent to client-sign).
      payout,
    };
    // include captured client photo file and/or preview when present (use effective value to respect parent capture)
    if (effectiveRequiredPhotoFile) {
      updatedClientData.clientPhotoFile = effectiveRequiredPhotoFile;
    }
    if (effectiveRequiredPhotoPreview) {
      updatedClientData.clientPhotoPreview = effectiveRequiredPhotoPreview;
    }
    onGenerate(updatedClientData);
  };

  // Resubmit using previously captured client signatures (for Awaiting Client with a note)
  const handleResubmit = () => {
    if (!termsScrolledToBottom) {
      setErrorMessage('Please scroll through the entire loan agreement before resubmitting.');
      return;
    }
    try {
      // Prefer references from current state; if empty, fall back to stored references_json
      let refs = effectiveReferences || {};
      const allEmpty = !Object.values(refs || {}).some(v => v && String(v).trim() !== '');
      if (allEmpty && signaturesData?.references_json) {
        try {
          const parsed = typeof signaturesData.references_json === 'string'
            ? JSON.parse(signaturesData.references_json)
            : signaturesData.references_json;
          refs = {
            reference1_name: parsed?.reference1_name || '',
            reference1_tel: parsed?.reference1_tel || '',
            reference2_name: parsed?.reference2_name || '',
            reference2_tel: parsed?.reference2_tel || ''
          };
        } catch {}
      }

      const borrowerSig = signature || signaturesData?.borrower_signature || null;
      const witnessSig = witness1Signature || signaturesData?.witness1_signature || null;
      const borrowerInit = borrowerInitials || signaturesData?.borrower_initials || null;
      const witnessInit = witness1Initials || signaturesData?.witness1_initials || null;

      const updatedClientData = {
        ...effectiveClientData,
        ...refs,
        signature: borrowerSig,
        witness1Signature: witnessSig,
        initials: {
          borrowerInitials: borrowerInit,
          witness1Initials: witnessInit
        },
        signatureLocations: (() => {
          try {
            const locs = signaturesData?.signature_locations;
            return locs ? (typeof locs === 'string' ? JSON.parse(locs) : locs) : signatureLocations;
          } catch { return signatureLocations; }
        })(),
        payout,
      };
      if (payoutMissing.length) {
        setErrorMessage(`How you want to receive the money: enter ${payoutMissing.join(', ')}.`);
        return;
      }

      // Basic presence check to avoid submitting empty signatures (the
      // borrower's only: the agreement has no Witness 1)
      if (!borrowerSig || !borrowerInit) {
        setErrorMessage('Previously captured signatures are incomplete. Please contact support or re-sign.');
        return;
      }
      onGenerate(updatedClientData);
    } catch (e) {
      setErrorMessage('Could not prepare resubmission payload. Please try again.');
    }
  };

  const formatDate = (dateString) => {
    // DD/MM/YYYY
    return formatDateDMY(dateString);
  };
  
  // The contract shows the loan's own figures as the API stored (or, for an
  // application not yet submitted, quoted) them — nothing is worked out here.
  const numOr = (v, fallback = null) => {
    const n = parseFloat(v);
    return Number.isFinite(n) ? n : fallback;
  };
  const principalNum = numOr(loanData?.principal_amount, 0);
  const principalPlusInterestNum = numOr(loanData?.principal_amount_plus_interest, 0);
  const interestAmountNum = numOr(loanData?.interest, Number((principalPlusInterestNum - principalNum).toFixed(2)));

  // The loan's expenses: those it carries (the quote's rows for a new
  // application), else the stored ones from GET /api/loans/:id/additional-expenses.
  // An empty array means no additional expenses.
  const toExpenseRows = (list) => (Array.isArray(list) ? list : []).map((exp, idx) => ({
    id: exp?.id || exp?.expense_code || idx,
    expense_name: exp?.expense_name || exp?.expense_code || 'Additional Expense',
    calculated_amount: Number(exp?.calculated_amount || 0),
  }));
  const [storedExpenses, setStoredExpenses] = useState(null);
  const hasOwnExpenses = Array.isArray(loanData?.additional_expenses);
  useEffect(() => {
    if (hasOwnExpenses || !loanData?.id) { setStoredExpenses(null); return undefined; }
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(apiUrl(`/api/loans/${loanData.id}/additional-expenses`), {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        });
        if (!res.ok) return;
        const data = await res.json();
        if (!cancelled) setStoredExpenses({ rows: Array.isArray(data?.expenses) ? data.expenses : [], total: numOr(data?.total, 0) });
      } catch { /* the contract still shows without the breakdown */ }
    })();
    return () => { cancelled = true; };
  }, [loanData?.id, hasOwnExpenses]);
  const expenseRows = toExpenseRows(hasOwnExpenses ? loanData.additional_expenses : storedExpenses?.rows);

  const additionalExpensesTotalNum = numOr(loanData?.additional_expenses_total, null)
    ?? (storedExpenses ? storedExpenses.total : Number(expenseRows.reduce((sum, exp) => sum + exp.calculated_amount, 0).toFixed(2)));
  const grandTotalObligationNum = numOr(loanData?.total_repayable, null)
    ?? Number((principalPlusInterestNum + additionalExpensesTotalNum).toFixed(2));
  const durationMonthsNum = Math.max(1, parseInt(loanData?.duration_months, 10) || 1);
  // The penalty clause as the API words it: the loan's own (loan.state;
  // null for a loan signed with the earlier wording) or, for an application
  // not yet created, the quote's. Without one the earlier wording shows.
  const penaltyClause = (loanData?.id ? loanData?.state?.penalty_clause : loanData?.penalty_clause) || null;
  const instalmentAmountWithExpensesNum = numOr(loanData?.monthly_payment, 0);
  const showScrollableTermsSection = isClientReview || ['Walk-in', 'Online'].includes(String(loanData?.application_type || 'Online'));

  const staffDocuments = !isClientReview ? [...fetchedDocuments, ...clientIdentificationDocs] : [];

  return (
    <>
  {/* client notes/documents are now rendered in the modal header (ClientDashboard) */}

      {/* No frame of its own: the agreement fills the width of the screen. */}
      <div className="contract-preview">
        {/* Title - Only visible for client review */}
  {showPreviewHeader && isClientReview && signatureEditingEnabled && (
          <div className="mb-5 sm:mb-6 overflow-hidden rounded-xl border border-blue-200 dark:border-blue-900/40 shadow-sm">
            <div className="h-1.5 bg-gradient-to-r from-blue-600 via-blue-500 to-cyan-500" />
            <div className="bg-[#1e3a5f] dark:bg-slate-900 px-4 sm:px-6 py-4 sm:py-5 text-center">
              <p className="text-[10px] sm:text-xs tracking-[0.18em] uppercase text-blue-200 dark:text-blue-300 font-semibold">Koniza Loan Agreement</p>
              <h1 className="text-2xl sm:text-3xl font-black text-white mt-1">LOAN AGREEMENT</h1>
              <p className="text-xs sm:text-sm text-blue-100 dark:text-slate-300 mt-2">Contract Preview - Please review before signing</p>
            </div>
          </div>
        )}
      
  {/* Loan Documents Section - Only shown for staff/admin in its own section */}
  {/* Only show Documents panel for staff/admin and not for Walk-in applications */}
  {/* hideDocuments: shown inside a screen that lists the documents already. */}
  {!isClientReview && !hideDocuments && loanData?.application_type !== 'Walk-in' && (
        <ContractSectionCard title="Documents">
          <div className="p-3 sm:p-4">
          {clientIdentificationDocs.length > 0 && (
            <p className="text-xs text-gray-500 dark:text-slate-400 mb-2">Includes loan uploads and client identification files.</p>
          )}

          {documentsLoading ? (
            <div className="py-6 text-center">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              <p className="mt-2 text-gray-600 dark:text-slate-300">Loading documents...</p>
            </div>
          ) : staffDocuments.length > 0 ? (
            <div className={DOCUMENT_GRID}>
              {staffDocuments.map((doc) => (
                <DocumentTile key={doc.id} type={doc.document_type} doc={doc} badge={doc.document_scope === 'client-id' ? 'Client' : undefined} />
              ))}
            </div>
          ) : (
            <div className="text-center p-6 bg-white dark:bg-slate-700 border border-gray-200 dark:border-slate-600 rounded-lg">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 text-gray-400 dark:text-slate-500 mx-auto mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 13h6m-3-3v6m5 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <p className="text-gray-600 dark:text-slate-300">No documents have been uploaded for this loan</p>
              <p className="text-sm text-gray-500 dark:text-slate-400 mt-1">Loan Code: {loanData?.loan_code || loanData?.loanCode || '-'}</p>
            </div>
          )}
          </div>
        </ContractSectionCard>

      )}

  {/* client notes/documents are now rendered above the contract card to avoid duplication */}

  {/* SECTION 1: Contract Terms - consistent scroll area for online and walk-in previews */}
    {showScrollableTermsSection && (
      // No frame around it: the agreement itself fills the width.
      <div className="mb-8">
          {/* Read-instruction banner sits outside the scrollable document */}
          {isClientReview && (
            <div className="bg-amber-50 border border-amber-200 rounded-t px-4 py-2 text-center text-xs text-amber-800">
              {signatureEditingEnabled
                ? 'Please scroll through and read the entire loan agreement before signing'
                : 'Review the full loan agreement'}
              {/* Until the client has scrolled to the end, submitting stays off. */}
              {!termsScrolledToBottom && (
                <span className="block mt-0.5 font-semibold text-red-600 dark:text-red-400">
                  Scroll through contract to allow submit
                </span>
              )}
            </div>
          )}

          {/* Scrollable document — styled to match the generated signed contract */}
          <div>
          <div className="w-full bg-white dark:bg-slate-800 border border-gray-300 dark:border-slate-600 shadow-sm">

            {/* Page chrome: accent bars matching the PDF */}
            <div className="h-[10px] bg-[#1a365d]" />
            <div className="h-[2px] bg-[#2563eb]" />
            {/* Company header block — full-width navy bar matching PDF cover page */}
            <div className="bg-[#1a365d] px-4 py-3 text-center" style={{ borderBottom: '3px solid #2563eb', fontFamily: 'Helvetica, Arial, sans-serif' }}>
              <p className="text-white font-black text-sm uppercase tracking-widest">
                {(companyConfig?.company_name || 'COMPANY NAME').toUpperCase()}
              </p>
              <p className="text-[#93c5fd] text-[11px] mt-1">Microlending Agreement — Strictly Confidential</p>
            </div>

            <div
              id="contract-terms"
              ref={termsContainerRef}
              onScroll={handleTermsScroll}
              className="p-4 sm:p-6 max-h-[360px] sm:max-h-[430px] overflow-y-auto leading-relaxed text-[13px] sm:text-sm text-gray-900 dark:text-slate-200 bg-white dark:bg-slate-800"
              style={{ fontFamily: 'Helvetica, Arial, sans-serif' }}
            >

            {/* Cover page content — matches PDF Page 1 layout */}
            <div>
              {/* Title badge — rounded, shadowed, matching PDF central badge */}
              <div className="py-5 sm:py-6 bg-white dark:bg-slate-800 text-center">
                <div className="relative inline-block">
                  {/* Shadow layer */}
                  <div className="absolute inset-0 rounded-lg bg-[#c7d2fe]" style={{ transform: 'translate(3px,3px)' }} />
                  {/* Badge */}
                  <div className="relative rounded-lg overflow-hidden border-2 border-[#1a365d] bg-[#eff6ff] dark:bg-slate-700" style={{ minWidth: '180px' }}>
                    <div className="h-[9px] bg-[#1a365d]" />
                    <div className="h-[2px] bg-[#2563eb]" />
                    <div className="px-10 py-3">
                      <h1 className="text-lg sm:text-2xl font-black text-[#1a365d] dark:text-blue-300 dark:text-blue-300 tracking-wider leading-tight">LOAN<br/>AGREEMENT</h1>
                    </div>
                  </div>
                </div>
              </div>

              {/* Date + decorative divider */}
              <div className="text-center pb-3 bg-white dark:bg-slate-800">
                <p className="text-[#64748b] dark:text-slate-400 text-xs sm:text-sm">Date: {formatDate(new Date())}</p>
                <div className="mx-auto mt-2" style={{ borderTop: '0.8px solid #2563eb', width: 'calc(100% - 100px)' }} />
              </div>

              {/* Parties intro */}
              <p className="text-center text-xs sm:text-sm font-medium text-[#1a202c] dark:text-slate-200 pt-2 pb-3 px-4">
                This Loan Agreement ("Agreement") is entered into between:
              </p>

              {/* Side-by-side party boxes — matching PDF drawPartyBox */}
              <div className="flex gap-3 sm:gap-5 px-3 sm:px-6 pb-6">
                {/* LENDER box */}
                <div className="flex-1 relative">
                  <div className="absolute inset-0 rounded-md bg-[#dde5f0] dark:bg-slate-600" style={{ transform: 'translate(2px,2px)' }} />
                  <div className="relative rounded-md overflow-hidden border border-[#1a365d] bg-[#f0f5ff] dark:bg-slate-700">
                    <div className="bg-[#1a365d] px-2 py-1.5 text-center">
                      <span className="text-white font-bold text-[11px] tracking-wide">LENDER</span>
                    </div>
                    <div className="h-[2px] bg-[#2563eb]" />
                    <div className="p-2 sm:p-3 text-center text-[10px] sm:text-xs text-[#1a202c] dark:text-slate-200 space-y-0.5">
                      <p>{companyConfig?.company_name || '______________________________'}</p>
                      {companyConfig?.address && <p>{companyConfig.address}</p>}
                      {companyConfig?.email && <p>Email: {companyConfig.email}</p>}
                      {companyConfig?.phone_number && <p>Tel: {companyConfig.phone_number}</p>}
                    </div>
                  </div>
                </div>

                {/* BORROWER box */}
                <div className="flex-1 relative">
                  <div className="absolute inset-0 rounded-md bg-[#dde5f0] dark:bg-slate-600" style={{ transform: 'translate(2px,2px)' }} />
                  <div className="relative rounded-md overflow-hidden border border-[#1a365d] bg-[#f0f5ff] dark:bg-slate-700">
                    <div className="bg-[#1a365d] px-2 py-1.5 text-center">
                      <span className="text-white font-bold text-[11px] tracking-wide">BORROWER</span>
                    </div>
                    <div className="h-[2px] bg-[#2563eb]" />
                    <div className="p-2 sm:p-3 text-center text-[10px] sm:text-xs text-[#1a202c] dark:text-slate-200 space-y-0.5">
                      <p>{effectiveClientData.first_name} {effectiveClientData.middle_name || ''} {effectiveClientData.last_name}</p>
                      <p>ID/Passport: {effectiveClientData.id_passport_no}</p>
                      <p>Phone: {effectiveClientData.phone_number}</p>
                      <p>Email: {effectiveClientData.email}</p>
                      <p>Address: {effectiveClientData.residential_address}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* ── PAGE 2: BORROWER'S PERSONAL INFORMATION ── */}
              {/* Main section header — full-width navy bar matching PDF sectionHeader() */}
              <div className="bg-[#1a365d] px-3 py-2 text-white font-bold text-[11px] uppercase tracking-wide mt-4">
                BORROWER'S PERSONAL INFORMATION
              </div>

              {/* Personal info fields — two-column, navy labels + #cbd5e0 underlines */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3 text-[11px] sm:text-xs px-2 pt-3 pb-2">
                <div className="flex items-baseline gap-2">
                  <span className="font-bold text-[#1a365d] dark:text-blue-300 shrink-0 w-28">Postal Address:</span>
                  <span className="flex-grow border-b border-[#cbd5e0] dark:border-slate-600 text-[#1a202c] dark:text-slate-200 min-w-0">{effectiveClientData.postal_address || ''}</span>
                </div>
                <div className="flex items-baseline gap-2">
                  <span className="font-bold text-[#1a365d] dark:text-blue-300 shrink-0 w-16">Tel no:</span>
                  <span className="flex-grow border-b border-[#cbd5e0] dark:border-slate-600 text-[#1a202c] dark:text-slate-200 min-w-0">{effectiveClientData.phone_number || ''}</span>
                </div>
                <div className="flex items-baseline gap-2 sm:col-span-2">
                  <span className="font-bold text-[#1a365d] dark:text-blue-300 shrink-0 w-28">Physical Address:</span>
                  <span className="flex-grow border-b border-[#cbd5e0] dark:border-slate-600 text-[#1a202c] dark:text-slate-200 min-w-0">{effectiveClientData.residential_address || ''}</span>
                </div>
                <div className="flex items-baseline gap-2">
                  <span className="font-bold text-[#1a365d] dark:text-blue-300 shrink-0 w-28">Marital Status:</span>
                  <span className="flex-grow border-b border-[#cbd5e0] dark:border-slate-600 text-[#1a202c] dark:text-slate-200 min-w-0">{effectiveClientData.marital_status || ''}</span>
                </div>
                <div className="flex items-baseline gap-2">
                  <span className="font-bold text-[#1a365d] dark:text-blue-300 shrink-0 w-28">I.D./Passport No:</span>
                  <span className="flex-grow border-b border-[#cbd5e0] dark:border-slate-600 text-[#1a202c] dark:text-slate-200 min-w-0">{effectiveClientData.id_passport_no || ''}</span>
                </div>
              </div>

              {/* Employment Details sub-header — #1e4d8f + MID_BLUE left accent */}
              <div className="flex items-stretch bg-[#1e4d8f] mt-3">
                <div className="w-1 bg-[#2563eb] shrink-0" />
                <span className="text-white font-bold text-[10px] uppercase tracking-wide px-3 py-1.5">EMPLOYMENT DETAILS</span>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3 text-[11px] sm:text-xs px-2 pt-2 pb-2">
                <div className="flex items-baseline gap-2">
                  <span className="font-bold text-[#1a365d] dark:text-blue-300 shrink-0 w-28">Occupation:</span>
                  <span className="flex-grow border-b border-[#cbd5e0] dark:border-slate-600 text-[#1a202c] dark:text-slate-200 min-w-0">{effectiveClientData.occupation || ''}</span>
                </div>
                <div className="flex items-baseline gap-2">
                  <span className="font-bold text-[#1a365d] dark:text-blue-300 shrink-0 w-28">Employer Tel no:</span>
                  <span className="flex-grow border-b border-[#cbd5e0] dark:border-slate-600 text-[#1a202c] dark:text-slate-200 min-w-0">{effectiveClientData.employer_phone || ''}</span>
                </div>
                <div className="flex items-baseline gap-2">
                  <span className="font-bold text-[#1a365d] dark:text-blue-300 shrink-0 w-28">Employer Name:</span>
                  <span className="flex-grow border-b border-[#cbd5e0] dark:border-slate-600 text-[#1a202c] dark:text-slate-200 min-w-0">{effectiveClientData.employer_name || ''}</span>
                </div>
                <div className="flex items-baseline gap-2">
                  <span className="font-bold text-[#1a365d] dark:text-blue-300 shrink-0 w-28">Employer Address:</span>
                  <span className="flex-grow border-b border-[#cbd5e0] dark:border-slate-600 text-[#1a202c] dark:text-slate-200 min-w-0">{effectiveClientData.employer_address || ''}</span>
                </div>
                <div className="flex items-baseline gap-2">
                  <span className="font-bold text-[#1a365d] dark:text-blue-300 shrink-0 w-28">Payslip No:</span>
                  <span className="flex-grow border-b border-[#cbd5e0] dark:border-slate-600 text-[#1a202c] dark:text-slate-200 min-w-0">{effectiveClientData.payslip_number || ''}</span>
                </div>
              </div>

              {/* Banking Details sub-header */}
              <div className="flex items-stretch bg-[#1e4d8f] mt-3">
                <div className="w-1 bg-[#2563eb] shrink-0" />
                <span className="text-white font-bold text-[10px] uppercase tracking-wide px-3 py-1.5">BANKING DETAILS</span>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3 text-[11px] sm:text-xs px-2 pt-2 pb-4">
                <div className="flex items-baseline gap-2">
                  <span className="font-bold text-[#1a365d] dark:text-blue-300 shrink-0 w-16">Bank:</span>
                  <span className="flex-grow border-b border-[#cbd5e0] dark:border-slate-600 text-[#1a202c] dark:text-slate-200 min-w-0">{effectiveClientData.bank_name || ''}</span>
                </div>
                <div className="flex items-baseline gap-2">
                  <span className="font-bold text-[#1a365d] dark:text-blue-300 shrink-0 w-16">Branch:</span>
                  <span className="flex-grow border-b border-[#cbd5e0] dark:border-slate-600 text-[#1a202c] dark:text-slate-200 min-w-0">{effectiveClientData.branch_code || ''}</span>
                </div>
                <div className="flex items-baseline gap-2">
                  <span className="font-bold text-[#1a365d] dark:text-blue-300 shrink-0 w-28">Bank Account No:</span>
                  <span className="flex-grow border-b border-[#cbd5e0] dark:border-slate-600 text-[#1a202c] dark:text-slate-200 min-w-0">{effectiveClientData.account_number || ''}</span>
                </div>
                <div className="flex items-baseline gap-2">
                  <span className="font-bold text-[#1a365d] dark:text-blue-300 shrink-0 w-28">Type of Account:</span>
                  <span className="flex-grow border-b border-[#cbd5e0] dark:border-slate-600 text-[#1a202c] dark:text-slate-200 min-w-0">{effectiveClientData.account_type || ''}</span>
                </div>
              </div>

              {/* ── LOAN DETAILS ── */}
              <div className="bg-[#1a365d] px-3 py-2 text-white font-bold text-[11px] uppercase tracking-wide mt-4">
                LOAN DETAILS
              </div>

              {/* 4-column table — alternating navy/#1e4d8f headers, gray/white body cells, shadow */}
              <div className="relative mt-0.5">
                <div className="absolute inset-0 bg-[#dde5f0] dark:bg-slate-600" style={{ transform: 'translate(2px,2px)' }} />
                <div className="relative border border-[#1a365d] overflow-x-auto">
                  {/* Header row */}
                  <div className="grid grid-cols-4 min-w-[420px]">
                    <div className="bg-[#1a365d] px-2 py-2 text-center text-white font-bold text-[10px] uppercase">LOAN AMOUNT</div>
                    <div className="bg-[#1e4d8f] px-2 py-2 text-center text-white font-bold text-[10px] uppercase border-l border-[#4a6fa5]">FINANCE CHARGES AT</div>
                    <div className="bg-[#1a365d] px-2 py-2 text-center text-white font-bold text-[10px] uppercase border-l border-[#4a6fa5]">TOTAL REPAYABLE</div>
                    <div className="bg-[#1e4d8f] px-2 py-2 text-center text-white font-bold text-[10px] uppercase border-l border-[#4a6fa5]">INSTALMENT AMOUNT</div>
                  </div>
                  {/* Body row */}
                  <div className="grid grid-cols-4 border-t border-[#1a365d] min-w-[420px]">
                    <div className="bg-[#f8fafc] dark:bg-slate-700 p-2 sm:p-3 text-[10px] sm:text-xs">
                      <p className="text-[#64748b] dark:text-slate-400">Paid to Borrower</p>
                      <p className="font-black text-[#1a365d] dark:text-blue-300 text-base sm:text-xl mt-1">{toCurrency(parseFloat(loanData.principal_amount))}</p>
                    </div>
                    <div className="bg-white dark:bg-slate-700 p-2 sm:p-3 text-[10px] sm:text-xs border-l border-[#cbd5e0] dark:border-slate-600">
                      <p className="text-[#64748b] dark:text-slate-400">{loanData.interest_rate}% Fixed Interest Rate</p>
                      <p className="font-black text-[#1a365d] dark:text-blue-300 text-sm sm:text-base mt-1">{toCurrency(interestAmountNum)}</p>
                      <p className="text-[#64748b] dark:text-slate-400 mt-1">Interest Amount</p>
                    </div>
                    <div className="bg-[#f8fafc] dark:bg-slate-700 p-2 sm:p-3 text-[10px] sm:text-xs border-l border-[#cbd5e0] dark:border-slate-600">
                      <p className="text-[#64748b] dark:text-slate-400">Principal + Interest</p>
                      <p className="font-black text-[#1a365d] dark:text-blue-300 text-sm sm:text-base mt-1">{toCurrency(principalPlusInterestNum)}</p>
                      <p className="text-[#64748b] dark:text-slate-400 mt-2">+ Additional Expenses</p>
                      {expenseRows.map((expense) => (
                        <p key={`col3-${expense.id}`} className="text-[#1a202c] dark:text-slate-300 text-[10px]">{expense.expense_name}: {toCurrency(Number(expense.calculated_amount || 0))}</p>
                      ))}
                      <p className="font-black text-[#1a365d] dark:text-blue-300 mt-2">Grand Total: {toCurrency(grandTotalObligationNum)}</p>
                    </div>
                    <div className="bg-white dark:bg-slate-700 p-2 sm:p-3 text-[10px] sm:text-xs border-l border-[#cbd5e0] dark:border-slate-600">
                      <p className="text-[#64748b] dark:text-slate-400">Per Month (Incl. Additional Expenses)</p>
                      <p className="font-black text-[#1a365d] dark:text-blue-300 text-sm sm:text-base mt-1">{toCurrency(instalmentAmountWithExpensesNum)}</p>
                      <p className="text-[#64748b] dark:text-slate-400 mt-2">First: <span className="text-[#1a202c] font-bold">{formatDate(loanData.start_date)}</span></p>
                      <p className="text-[#64748b] dark:text-slate-400">Last: <span className="text-[#1a202c] font-bold">{formatDate(loanData.due_date)}</span></p>
                      <p className="text-[#64748b] dark:text-slate-400">{durationMonthsNum} {durationMonthsNum === 1 ? 'instalment' : 'instalments'} / Monthly</p>
                    </div>
                  </div>
                  {/* Penalty row — amber tint matching PDF #fff8e6 / #f59e0b border */}
                  <div className="bg-[#fff8e6] dark:bg-amber-900/20 border-t border-[#f59e0b] p-2 sm:p-3 text-[10px] sm:text-xs min-w-[420px]">
                    {penaltyClause ? (
                      <p className="text-[#1a202c] dark:text-slate-200">{penaltyClause}</p>
                    ) : (
                      <>
                        <p className="text-[#1a202c] dark:text-slate-200">Penalty interest will be charged at <span className="font-bold text-[#b45309] dark:text-amber-400 underline">5%</span> per month on the outstanding total loan amount (including additional expenses).</p>
                        <p className="text-[#64748b] dark:text-slate-400 italic mt-1">(The penalty interest may not exceed 5% per month and may not be charged for a period of more than three (3) months).</p>
                      </>
                    )}
                  </div>
                  {/* Additional expenses sub-section */}
                  <div className="border-t border-[#1a365d] min-w-[420px]">
                    <div className="flex items-stretch bg-[#1e4d8f]">
                      <div className="w-1 bg-[#2563eb] shrink-0" />
                      <span className="text-white font-bold text-[10px] uppercase tracking-wide px-3 py-1.5">ADDITIONAL LOAN EXPENSES</span>
                    </div>
                    <table className="w-full text-[10px] sm:text-xs">
                      <thead>
                        <tr className="bg-[#1e4d8f] dark:bg-slate-600 border-b border-[#cbd5e0] dark:border-slate-500">
                          <th className="text-left px-3 py-1.5 font-bold text-white">Description</th>
                          <th className="text-right px-3 py-1.5 font-bold text-white">Amount (N$)</th>
                        </tr>
                      </thead>
                      <tbody>
                        {expenseRows.map((expense) => (
                          <tr key={expense.id} className="border-b border-[#cbd5e0] dark:border-slate-600">
                            <td className="px-3 py-1.5 text-[#1a202c] dark:text-slate-200">{expense.expense_name}</td>
                            <td className="text-right px-3 py-1.5 text-[#1a202c] dark:text-slate-200">{Number(expense.calculated_amount || 0).toFixed(2)}</td>
                          </tr>
                        ))}
                        <tr className="bg-[#f0f5ff] dark:bg-slate-600 border-t border-[#1a365d] dark:border-slate-500">
                          <td className="px-3 py-1.5 font-bold text-[#1a365d] dark:text-blue-200">Total Additional Expenses</td>
                          <td className="text-right px-3 py-1.5 font-bold text-[#1a365d] dark:text-blue-200">{additionalExpensesTotalNum.toFixed(2)}</td>
                        </tr>
                        <tr className="bg-[#1a365d]">
                          <td className="px-3 py-1.5 font-bold text-white text-[9px] sm:text-[10px]">Grand Total Loan Obligation (Principal + Interest + Expenses)</td>
                          <td className="text-right px-3 py-1.5 font-bold text-white">{grandTotalObligationNum.toFixed(2)}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>

              {/* ── PERIOD OF LOAN ── */}
              <div className="bg-[#1a365d] px-3 py-2 text-white font-bold text-[11px] uppercase tracking-wide mt-5">
                PERIOD OF LOAN
              </div>
              <div className="px-2 pt-3 pb-2">
                <p className="text-[#1a202c] text-[11px] sm:text-xs mb-3">The Borrower shall repay the capital amount including interest and additional expenses (as stated above) on or before:</p>

                {/* Due-date highlight box — matches PDF roundedRect with shadow + MID_BLUE left accent */}
                <div className="relative inline-block mb-4">
                  <div className="absolute inset-0 rounded-md bg-[#dde5f0] dark:bg-slate-600" style={{ transform: 'translate(2px,2px)' }} />
                  <div className="relative rounded-md overflow-hidden border border-[#1a365d] bg-[#eff6ff] dark:bg-slate-700 flex">
                    <div className="w-1.5 bg-[#2563eb] shrink-0" />
                    <div className="px-3 py-2">
                      <p className="text-[#64748b] dark:text-slate-400 text-[9px] uppercase tracking-wide">FINAL REPAYMENT DATE</p>
                      <p className="font-black text-[#1a365d] dark:text-blue-300 text-sm sm:text-base">{formatDate(loanData.due_date)}</p>
                    </div>
                  </div>
                </div>

                <TermsAndConditions />
                <br />
                <div className="my-4">
                  <ClientInformationConsentAgreement
                    companyName={companyConfig?.company_name || ''}
                    clientAddress={effectiveClientData?.residential_address || ''}
                    clientName={[effectiveClientData?.first_name, effectiveClientData?.middle_name, effectiveClientData?.last_name].filter(Boolean).join(' ')}
                  />
                  <DebitOrderConsentAgreement
                    companyName={companyConfig?.company_name || ''}
                    clientAddress={effectiveClientData?.residential_address || ''}
                    clientName={[effectiveClientData?.first_name, effectiveClientData?.middle_name, effectiveClientData?.last_name].filter(Boolean).join(' ')}
                    amount={loanData?.monthly_payment ? Number(loanData.monthly_payment).toFixed(2) : '____'}
                    dayOfMonth={(loanData?.start_date ? new Date(loanData.start_date).getDate() : '____').toString()}
                    months={loanData?.duration_months || '____'}
                    startDate={loanData?.start_date ? formatDate(loanData.start_date) : '______________________________'}
                    bankName={effectiveClientData?.bank_name}
                    branchCode={effectiveClientData?.branch_code}
                    accountNumber={effectiveClientData?.account_number}
                    accountType={effectiveClientData?.account_type}
                  />
                </div>
                <div className="my-6 border-t pt-4 border-[#cbd5e0] dark:border-slate-600"><AnnexureA /></div>
                <p className="mb-6 text-[#1a202c] dark:text-slate-200 text-[11px] sm:text-xs">I acknowledge that this agreement has been completed in full prior to my signature.</p>
                {!termsScrolledToBottom && isClientReview && signatureEditingEnabled && (
                  <div className="sticky bottom-0 bg-[#eff6ff] dark:bg-slate-700 border-t border-[#1a365d] text-[#1a365d] p-2 text-center text-xs font-medium">Please scroll to the end to acknowledge you have read the entire loan agreement</div>
                )}
              </div>
            </div>
            </div>{/* end scroll container */}
            {/* Document footer — matches the signed contract */}
            <div className="border-t border-gray-300 px-4 py-1.5 text-[10px] text-gray-500 flex items-center justify-between" style={{ fontFamily: 'Helvetica, Arial, sans-serif' }}>
              <span>CONFIDENTIAL — LOAN AGREEMENT</span>
              <span>{isClientReview && signatureEditingEnabled ? 'Preview' : 'Signed Copy'}</span>
            </div>
          </div>
          </div>
      </div>
      )}

      {/* SECTION 2: References and Signatures */}
      {showReferenceSection && (
        <div className="mt-8">
          <div>

          {/* References section */}
          <SigningPart id="contract-references" title="References" required>
            <div>
              {(() => {
                // Only warn about what is actually missing (a dial code alone is not a number).
                const r = effectiveReferences || {};
                const telOk = (t) => String(t || '').replace(/\D/g, '').length >= 8;
                const missing = [];
                if (!String(r.reference1_name || '').trim()) missing.push('reference 1 name');
                if (!telOk(r.reference1_tel)) missing.push('reference 1 tel no');
                if (!String(r.reference2_name || '').trim()) missing.push('reference 2 name');
                if (!telOk(r.reference2_tel)) missing.push('reference 2 tel no');
                return missing.length ? (
                  <p className="text-xs text-red-500 mb-3">Still needed: {missing.join(', ')}</p>
                ) : null;
              })()}

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-4 text-sm">
                <div className="flex flex-col">
                  <div className="flex items-center mb-1">
                    <p className="font-medium w-6">1.</p>
                    <p className="font-medium">Name: <span className="text-red-500">*</span></p>
                  </div>
                  <input
                    type="text"
                    name="reference1_name"
                    value={effectiveReferences.reference1_name}
                    onChange={handleReferenceChange}
                    className={`${!effectiveReferences.reference1_name ? 'border-red-400 dark:border-red-500' : ''} w-full`}
                    placeholder="Enter reference name"
                    required
                  />
                </div>

                <div className="flex flex-col">
                  <div className="flex items-center mb-1">
                    <p className="font-medium">Tel No: <span className="text-red-500">*</span></p>
                  </div>
                  <PhoneInput
                    value={effectiveReferences.reference1_tel || ''}
                    onChange={(combined) => {
                      if (typeof effectiveSetReferences === 'function') {
                        effectiveSetReferences(prev => ({ ...prev, reference1_tel: combined }));
                      }
                    }}
                    required
                  />
                </div>

                <div className="flex flex-col">
                  <div className="flex items-center mb-1">
                    <p className="font-medium w-6">2.</p>
                    <p className="font-medium">Name: <span className="text-red-500">*</span></p>
                  </div>
                  <input
                    type="text"
                    name="reference2_name"
                    value={effectiveReferences.reference2_name}
                    onChange={handleReferenceChange}
                    className={`${!effectiveReferences.reference2_name ? 'border-red-400 dark:border-red-500' : ''} w-full`}
                    placeholder="Enter reference name"
                    required
                  />
                </div>

                <div className="flex flex-col">
                  <div className="flex items-center mb-1">
                    <p className="font-medium">Tel No: <span className="text-red-500">*</span></p>
                  </div>
                  <PhoneInput
                    value={effectiveReferences.reference2_tel || ''}
                    onChange={(combined) => {
                      if (typeof effectiveSetReferences === 'function') {
                        effectiveSetReferences(prev => ({ ...prev, reference2_tel: combined }));
                      }
                    }}
                    required
                  />
                </div>
              </div>
            </div>
          </SigningPart>

          {/* How the money is paid out once approved: mandatory. */}
          <SigningPart id="contract-payout" title="How would you like to receive the money?" required>
            <div>
              {payoutMissing.length > 0 && (
                <p className="mb-3 text-xs text-red-600 dark:text-red-400">Required: {payoutMissing.join(', ')}.</p>
              )}
              {(() => {
                // The same look as the References above; nothing is filled in
                // until the client asks for their profile's details.
                const input = (empty) => `border ${empty ? 'border-red-400 dark:border-red-500' : 'border-gray-300 dark:border-slate-500'} rounded py-2 px-3 w-full bg-gray-50 dark:bg-slate-600 dark:text-slate-100 dark:placeholder-slate-400 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500`;
                const button = 'px-4 py-1.5 bg-blue-600 text-white rounded hover:bg-blue-700 text-sm transition';
                const set = (k) => (e) => setPayout((prev) => ({ ...prev, [k]: e.target.value }));
                const c = effectiveClientData || {};
                const blank = (v) => !String(v || '').trim();
                const types = ACCOUNT_TYPES;
                // The client's bank as saved, kept as a choice when it is not in the list.
                const banks = NAMIBIAN_BANKS.concat(payout.bank_name && !NAMIBIAN_BANKS.includes(payout.bank_name) ? [payout.bank_name] : []);
                return (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-4 text-sm">
                    <div className="flex flex-col">
                      <p className="font-medium mb-1">Payout method: <span className="text-red-500">*</span></p>
                      <select
                        className={input(!payout.method)}
                        value={payout.method}
                        onChange={(e) => setPayout((prev) => ({ ...prev, method: e.target.value }))}
                      >
                        <option value="">Choose…</option>
                        <option value="eWallet">eWallet</option>
                        <option value="EFT">EFT (bank transfer)</option>
                      </select>
                    </div>

                    {payout.method === 'eWallet' && (
                      <div className="flex flex-col">
                        <p className="font-medium mb-1">eWallet cellphone number: <span className="text-red-500">*</span></p>
                        <PhoneInput
                          value={payout.ewallet_number || ''}
                          onChange={(combined) => setPayout((prev) => ({ ...prev, ewallet_number: combined }))}
                          required
                        />
                        <div className="mt-2">
                          <button
                            type="button"
                            className={button}
                            onClick={() => setPayout((prev) => ({ ...prev, ewallet_number: c.phone_number || '' }))}
                          >
                            Use my current cellphone number
                          </button>
                        </div>
                      </div>
                    )}

                    {payout.method === 'EFT' && (
                      <>
                        <div className="flex flex-col sm:justify-end">
                          <div>
                            <button
                              type="button"
                              className={button}
                              onClick={() => setPayout((prev) => ({
                                ...prev,
                                bank_name: c.bank_name || '',
                                branch_code: c.branch_code || '',
                                account_number: c.account_number || '',
                                account_type: c.account_type || '',
                                account_holder: [c.first_name, c.middle_name, c.last_name].filter(Boolean).join(' '),
                              }))}
                            >
                              Use my current banking details
                            </button>
                          </div>
                        </div>
                        <div className="flex flex-col">
                          <p className="font-medium mb-1">Bank: <span className="text-red-500">*</span></p>
                          <select className={input(blank(payout.bank_name))} value={payout.bank_name} onChange={set('bank_name')}>
                            <option value="">Choose…</option>
                            {banks.map((b) => <option key={b} value={b}>{b}</option>)}
                          </select>
                        </div>
                        <div className="flex flex-col">
                          <p className="font-medium mb-1">Branch code: <span className="text-gray-400 font-normal">(optional)</span></p>
                          <input type="text" className={input(false)} value={payout.branch_code} onChange={set('branch_code')} placeholder="Enter the branch code" />
                        </div>
                        <div className="flex flex-col">
                          <p className="font-medium mb-1">Account number: <span className="text-red-500">*</span></p>
                          <input type="text" inputMode="numeric" className={input(!/^\d{5,20}$/.test(String(payout.account_number || '').replace(/\s/g, '')))} value={payout.account_number} onChange={set('account_number')} placeholder="Digits only" />
                        </div>
                        <div className="flex flex-col">
                          <p className="font-medium mb-1">Account type: <span className="text-red-500">*</span></p>
                          <select className={input(!payout.account_type)} value={payout.account_type} onChange={set('account_type')}>
                            <option value="">Choose…</option>
                            {types.concat(payout.account_type && !types.includes(payout.account_type) ? [payout.account_type] : [])
                              .map((t) => <option key={t} value={t}>{t}</option>)}
                          </select>
                        </div>
                        <div className="flex flex-col sm:col-span-2">
                          <p className="font-medium mb-1">Account holder: <span className="text-red-500">*</span></p>
                          <input type="text" className={input(blank(payout.account_holder))} value={payout.account_holder} onChange={set('account_holder')} placeholder="Name on the account" />
                        </div>
                      </>
                    )}
                  </div>
                );
              })()}
            </div>
          </SigningPart>

          {/* Recapture controls shown when clients are viewing read-only signatures */}
          {isReadOnly && canResubmit && !needsFirstSignatures && !recaptureMode && (
            <div className="mt-6 p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700/40 rounded-lg flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div>
                <p className="text-sm text-amber-900 dark:text-amber-200 font-medium">Need to update your signature?</p>
                <p className="text-xs text-amber-800 dark:text-amber-300">Tap recapture to clear the locked signatures and draw fresh ones before resubmitting.</p>
              </div>
              <button
                type="button"
                onClick={handleStartRecapture}
                className="self-start sm:self-auto px-4 py-2 bg-amber-600 text-white rounded-md text-sm font-semibold hover:bg-amber-700 transition"
              >
                Recapture Signatures
              </button>
            </div>
          )}
          {isReadOnly && canResubmit && !needsFirstSignatures && recaptureMode && (
            <div className="mt-6 p-4 bg-slate-50 dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-lg flex flex-col gap-2">
              <p className="text-sm text-slate-900 dark:text-slate-100 font-medium">Recapture mode enabled</p>
              <p className="text-xs text-slate-700 dark:text-slate-300">Your previous signatures were cleared. Draw new signatures and initials below, then submit. If you change your mind, you can restore the prior signatures.</p>
              <div className="flex flex-col sm:flex-row gap-2">
                <button
                  type="button"
                  onClick={handleCancelRecapture}
                  className="px-4 py-2 bg-gray-200 dark:bg-slate-600 text-gray-800 dark:text-slate-100 rounded-md text-sm font-medium hover:bg-gray-300 dark:hover:bg-slate-500 transition"
                >
                  Use Previous Signatures
                </button>
                <button
                  type="button"
                  onClick={clearClientSignatureState}
                  className="px-4 py-2 bg-white dark:bg-slate-700 border border-gray-300 dark:border-slate-500 text-gray-700 dark:text-slate-200 rounded-md text-sm font-medium hover:bg-gray-50 dark:hover:bg-slate-600 transition"
                >
                  Clear All Pads
                </button>
              </div>
            </div>
          )}

          {signatureEditingEnabled && (
            <>
              {/* Signatures: their own heading, where and when, then the
                  borrower's signature and initials in one row. */}
              <div>
                <SigningPart title="Signatures" required className="mb-0">
                <div>
                  <div className="mb-5 flex flex-wrap items-center gap-x-3 gap-y-2 rounded-md bg-slate-50 dark:bg-slate-700/60 border border-slate-200 dark:border-slate-600 px-4 py-3 text-sm">
                    <span className="font-medium text-slate-700 dark:text-slate-200">Signed at <span className="text-red-500">*</span></span>
                    <TownInput
                      id="signed-at-town"
                      className="w-60"
                      value={signatureLocations.borrowerLocation}
                      onChange={(town) => setSignatureLocations((prev) => ({ ...prev, borrowerLocation: town }))}
                    />
                    <span className="text-slate-700 dark:text-slate-200">on</span>
                    <span className="font-semibold text-slate-900 dark:text-slate-100">
                      {new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}
                    </span>
                  </div>

                  <div className={`grid grid-cols-1 ${showLenderBlock ? 'xl:grid-cols-2' : ''} gap-6`}>
                    <div className="flex flex-col sm:flex-row sm:items-start gap-6">
                      <SignatureField id="borrower-signature" label="Borrower's signature" value={signature} onChange={setSignature} required />
                      <SignatureField id="borrower-initials" label="Borrower's initials" kind="initials" value={borrowerInitials} onChange={setBorrowerInitials} required />
                    </div>

                    {/* Lender (Walk-in, or Online in Awaiting Staff) */}
                    {showLenderBlock && (
                      <div>
                        <div className="flex flex-col sm:flex-row sm:items-start gap-6">
                          <SignatureField id="lender-signature" label="Lender's signature" value={lenderSignature} onChange={setLenderSignature} />
                          <SignatureField id="lender-initials" label="Lender's initials" kind="initials" value={lenderInitials} onChange={setLenderInitials} />
                        </div>
                        <p className="mt-2 text-xs text-gray-500 dark:text-slate-400">
                          {loanData?.application_type === 'Walk-in'
                            ? 'For Walk-in applications, the lender can sign at this stage.'
                            : 'For Online loans in Awaiting Staff, the lender can sign at this stage.'}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
                </SigningPart>
              </div>
            </>
          )}
          </div>
        </div>
      )}

          {/* Previously captured signatures (shown during client review when a staff note exists) */}
          {isReadOnly && canResubmit && !needsFirstSignatures && (
            <div className="mt-6 bg-white dark:bg-slate-700 p-4 rounded-lg border border-gray-200 dark:border-slate-600 shadow-sm dark:shadow-none">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-3 border-b dark:border-slate-500 pb-1">
                <div>
                  <h3 className="text-lg font-semibold dark:text-slate-100">Your previously captured signatures</h3>
                </div>
              </div>
              <div className="flex flex-col sm:flex-row sm:items-start gap-6">
                {[
                  ['borrowerSignature', "Borrower's signature", 'signature', signature, setSignature],
                  ['borrowerInitials', "Borrower's initials", 'initials', borrowerInitials, setBorrowerInitials],
                ].map(([field, label, kind, value, set]) => (
                  <SignatureField
                    key={field}
                    label={label}
                    kind={kind}
                    value={value}
                    onChange={set}
                    actions={!value && getStoredFieldValue(field) ? (
                      <button type="button" onClick={() => cancelSingleFieldRecapture(field)} className={SIGNATURE_BUTTON_PLAIN}>Use stored</button>
                    ) : null}
                  />
                ))}
              </div>
            </div>
          )}
      {/* Action Buttons */}
      <div className="flex flex-col sm:flex-row justify-end space-y-2 sm:space-y-0 sm:space-x-4 mt-8">
        <button
          type="button"
          onClick={onEdit}
          className="px-5 sm:px-6 py-2.5 bg-gray-200 dark:bg-slate-700 text-gray-800 dark:text-slate-200 rounded-md hover:bg-gray-300 dark:hover:bg-slate-600 text-sm sm:text-base font-medium transition shadow-sm dark:shadow-none"
        >
          {isReadOnly ? 'Close' : 'Edit'}
        </button>
        {signatureEditingEnabled ? (
          <button
            type="button"
            onClick={handleGenerate}
            disabled={loading || clientProfileLoading || (isClientReview && !termsScrolledToBottom)}
            className={`px-5 sm:px-6 py-2.5 ${
              loading || clientProfileLoading || (isClientReview && !termsScrolledToBottom) ? 'bg-blue-300 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700'
            } text-white rounded-md text-sm sm:text-base font-medium transition shadow-sm`}
          >
            {loading ? 'Generating...' : clientProfileLoading ? 'Loading client…' : 'Submit Contract'}
          </button>
        ) : (
          canResubmit && (
            <button
              type="button"
              onClick={handleResubmit}
              disabled={loading || !termsScrolledToBottom}
              className={`px-5 sm:px-6 py-2.5 ${
                loading || !termsScrolledToBottom ? 'bg-blue-300 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700'
              } text-white rounded-md text-sm sm:text-base font-medium transition shadow-sm`}
            >
              {loading ? 'Submitting…' : 'Resubmit for Review'}
            </button>
          )
        )}
  </div>
      
  {/* Help text for disabled button */}
  {(isClientReview && !termsScrolledToBottom && (signatureEditingEnabled || canResubmit)) && (
        <div className="mt-2 text-xs text-center text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/20 p-2 rounded-md border border-amber-200 dark:border-amber-700/40">
          {signatureEditingEnabled
            ? 'Please scroll through the entire loan agreement to enable the submit button'
            : 'Please scroll through the entire loan agreement to enable the resubmit button'}
        </div>
      )}

      {notice && (
        <div className="mt-4 p-4 bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-300 rounded-md border border-emerald-200 dark:border-emerald-800/40">
          {notice}
        </div>
      )}

      {/* Display error message if exists */}
      {errorMessage && (
        <div className="mt-4 p-4 bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 rounded-md border border-red-200 dark:border-red-800/40">
          {errorMessage}
        </div>
      )}
    </div>
    </>
  );
};

export default ContractPreview;
